// client/src/dnd/emitter_tool/engine/engine_parts/emitter/spawnSampler.js
import {
  DEG2RAD, clamp, rand, normalize, rotate,
  tangentCCW, angleOf, sampleInCircle, sampleOnCircle,
  sampleInArcDeg, sampleOnArcDeg,
} from "./shapeMath";

export function sampleSpawn({
  shape,
  shapeSettings = {},
  emissionMode = "area",
  edgeBehavior = "out",
  addRotDeg = 0,
  worldUnitsToPixels,
  origin,
}) {
  const px = worldUnitsToPixels;
  const o = origin || { x: 0, y: 0 };

  const em = String(
    shapeSettings.spawnFrom ??
    shapeSettings.emissionMode ??
    emissionMode ??
    "area"
  ).toLowerCase();

  const eb = String(
    shapeSettings.edgeBehavior ??
    edgeBehavior ??
    "out"
  ).toLowerCase();

  const finalize = (lx, ly, nux, nuy) => {
    let nx = nux, ny = nuy;
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) {
      const v = normalize(lx, ly);
      nx = v.x; ny = v.y;
    }
    const t = tangentCCW(nx, ny);
    return {
      x: o.x + lx,
      y: o.y + ly,
      normal: { x: nx, y: ny },
      tangent: normalize(t.x, t.y),
      suggestedDirRad: angleOf(nx, ny),
    };
  };

  switch (shape) {
    case "dot": {
      return finalize(0, 0, 1, 0);
    }

    case "circle": {
      const R = px(Math.max(0, Number(shapeSettings.radius ?? 1)));
      if (em === "edge") {
        const { x, y } = sampleOnCircle(R);
        const n = normalize(x, y);
        return finalize(x, y, n.x, n.y);
      } else {
        const { x, y } = sampleInCircle(R);
        const n = normalize(x, y);
        return finalize(x, y, n.x, n.y);
      }
    }

    case "arc": {
      const R = px(Math.max(0, Number(shapeSettings.radius ?? 1)));
      const startDeg = Number(shapeSettings.startDeg ?? 0) + addRotDeg;
      const endDeg   = Number(shapeSettings.endDeg   ?? 90) + addRotDeg;
      if (em === "edge") {
        const s = sampleOnArcDeg(startDeg, endDeg, R);
        const n = normalize(s.x, s.y);
        return finalize(s.x, s.y, n.x, n.y);
      } else {
        const s = sampleInArcDeg(startDeg, endDeg, R);
        const n = normalize(s.x, s.y);
        return finalize(s.x, s.y, n.x, n.y);
      }
    }

    case "square": {
      const w = px(Math.max(0, Number(shapeSettings.width  ?? 2)));
      const h = px(Math.max(0, Number(shapeSettings.height ?? 2)));
      const rotDeg = Number(shapeSettings.rotationDeg || 0) + (shapeSettings.rotationAnimEnabled ? addRotDeg : 0);

      if (em === "edge") {
        const perim = 2 * (w + h);
        const r = Math.random() * perim;
        let lx, ly, nx, ny;
        if (r < w) {
          lx = -w/2 + r; ly = -h/2; nx = 0; ny = -1;
        } else if (r < w + h) {
          lx =  w/2; ly = -h/2 + (r - w); nx = 1; ny = 0;
        } else if (r < w + h + w) {
          lx =  w/2 - (r - (w + h)); ly = h/2; nx = 0; ny = 1;
        } else {
          lx = -w/2; ly =  h/2 - (r - (w + h + w)); nx = -1; ny = 0;
        }
        const p = rotate(lx, ly, rotDeg);
        const n = rotate(nx, ny, rotDeg);
        return finalize(p.x, p.y, n.x, n.y);
      } else {
        const lx = (Math.random() - 0.5) * w;
        const ly = (Math.random() - 0.5) * h;
        const p = rotate(lx, ly, rotDeg);
        const n = normalize(p.x, p.y);
        return finalize(p.x, p.y, n.x, n.y);
      }
    }

    case "triangle": {
      const base = px(Math.max(0, Number(shapeSettings.width  ?? 2)));
      const height = px(Math.max(0, Number(shapeSettings.height ?? 2)));
      const rotDeg = Number(shapeSettings.rotationDeg || 0) + (shapeSettings.rotationAnimEnabled ? addRotDeg : 0);

      const A = { x: -base / 2, y: 0 };
      const B = { x:  base / 2, y: 0 };
      const C = { x: 0,          y: -height };

      if (em === "edge") {
        const lenAB = Math.hypot(B.x - A.x, B.y - A.y);
        const lenBC = Math.hypot(C.x - B.x, C.y - B.y);
        const lenCA = Math.hypot(A.x - C.x, A.y - C.y);
        const per = lenAB + lenBC + lenCA;
        let t = Math.random() * per;
        let P0, P1;

        if (t < lenAB) {
          P0 = A; P1 = B;
        } else if (t < lenAB + lenBC) {
          P0 = B; P1 = C;
        } else {
          P0 = C; P1 = A;
        }
        const u = Math.random();
        const lx = P0.x + (P1.x - P0.x) * u;
        const ly = P0.y + (P1.y - P0.y) * u;

        const ex = P1.x - P0.x, ey = P1.y - P0.y;
        let nx = -ey, ny = ex;
        const p = rotate(lx, ly, rotDeg);
        const n = rotate(nx, ny, rotDeg);
        const nUnit = normalize(n.x, n.y);
        return finalize(p.x, p.y, nUnit.x, nUnit.y);
      } else {
        let u = Math.random(), v = Math.random();
        if (u + v > 1) { u = 1 - u; v = 1 - v; }
        const lx = A.x + (B.x - A.x) * u + (C.x - A.x) * v;
        const ly = A.y + (B.y - A.y) * u + (C.y - A.y) * v;
        const p = rotate(lx, ly, rotDeg);
        const n = normalize(p.x, p.y);
        return finalize(p.x, p.y, n.x, n.y);
      }
    }

    case "cone": {
      const len = px(Math.max(0, Number(shapeSettings.radius ?? 1)));
      const spreadDeg = Number(shapeSettings.spreadDeg ?? 30);
      const baseDeg = Number(shapeSettings.rotationDeg || 0) + addRotDeg;
      const half = (spreadDeg * DEG2RAD) * 0.5;
      const a = baseDeg * DEG2RAD + rand(-half, half);
      const d = Math.random() * len;
      const lx = Math.cos(a) * d;
      const ly = Math.sin(a) * d;
      const n = normalize(lx, ly);
      return finalize(lx, ly, n.x, n.y);
    }

    case "line": {
      const L = px(Math.max(0, Number(shapeSettings.width ?? 2)));
      const rotDeg = Number(shapeSettings.rotationDeg || 0) + (shapeSettings.rotationAnimEnabled ? addRotDeg : 0);
      const t = Math.random() - 0.5;
      const lx = t * L;
      const ly = 0;
      const p = rotate(lx, ly, rotDeg);
      const nLocal = { x: 0, y: 1 };
      const n = rotate(nLocal.x, nLocal.y, rotDeg);
      const nUnit = normalize(n.x, n.y);
      return finalize(p.x, p.y, nUnit.x, nUnit.y);
    }

    case "random":
    default: {
      const R = px(Math.max(0, Number(shapeSettings.radius ?? 1)));
      const s = (em === "edge") ? sampleOnCircle(R) : sampleInCircle(R);
      const n = normalize(s.x, s.y);
      return finalize(s.x, s.y, n.x, n.y);
    }
  }
}

export default sampleSpawn;
